<script setup>
import { ref, computed } from 'vue'
import LibraryCard from './LibraryCard.vue'
import RemoveResourceButton from './RemoveResourceButton.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import DynamicHeading from '@/components/DynamicHeading.vue'
import { getStrategyUrl } from '@/util/links'
import CardContributor from './CardContributor.vue'
import { useContributorVisibility } from './composables/useContributorVisibility'

const props = defineProps({
  strategy: {
    type: Object,
    required: true
  },
  removable: {
    type: Boolean,
    default: true
  },
  saved: {
    type: Boolean,
    default: false
  },
  shared: {
    type: Boolean,
    default: false
  },
  clickable: {
    type: Boolean,
    default: true
  },
  headingLevel: {
    type: Number,
    default: 3
  }
})

const strategyUrl = computed(() => {
  const url = getStrategyUrl({
    id: props.strategy.content_uuid,
    isShared: props.shared,
    isSaved: props.saved
  })
  return url
})

const plainTextDescription = computed(() => {
  return wikiToPlainText(props.strategy.content_body.source_strategy.description)
})

const resourceType = ref(CONTENT_TYPES.SAVED_STRATEGY)

const { contributorIsVisible } = useContributorVisibility(props.strategy.content_uuid)
</script>
<template>
  <div
    class="relative [&_.remove-resource-button]:hover:opacity-100 [&_.remove-resource-button]:focus:opacity-100 [&_.card-title]:hover:text-gray-1000 [&_.card-title]:focus:text-gray-1000"
  >
    <LibraryCard
      :content-uuid="strategy.content_uuid"
      :show-folder-name="strategy.show_folder_name"
      :folder-name="strategy.collection_body?.name"
      :url="strategyUrl"
      :updated-time="strategy.content_body.updated_at"
      :ariaLabel="
        strategy.content_body.source_strategy.title +
        ' ' +
        strategy.content_body.source_strategy.description.substring(0, 100)
      "
      v-bind:clickable="clickable"
    >
      <div class="flex">
        <img
          class="max-w-24 self-start"
          :src="strategy.content_body.source_strategy.image_url"
          :alt="strategy.content_body.source_strategy.title"
        />
        <div class="pl-4">
          <DynamicHeading
            :headingLevel="props.headingLevel"
            :class="`${contributorIsVisible ? 'pb-1' : 'pb-3'} text-xl leading-5 text-primary-500 card-title`"
            data-cy="strategy-title"
          >
            {{ strategy.content_body.source_strategy.title }}
          </DynamicHeading>
          <CardContributor
            v-if="!shared"
            :contentId="strategy.content_uuid"
          />
          <p
            class="overflow-hidden text-ellipsis text-md [-webkit-box-orient:vertical] [-webkit-line-clamp:4] [display:-webkit-box]"
          >
            {{ plainTextDescription }}
          </p>
        </div>
      </div>
      <template #footer-right>
        <span class="flex items-center text-sm font-bold resource-count" v-if="strategy.content_body.source_strategy.resource_count">
          <span class="material-icons-outlined text-lg">file_download</span>
          {{ strategy.content_body.source_strategy.resource_count }}
        </span>
      </template>
    </LibraryCard>
    <RemoveResourceButton
      v-if="!shared && removable"
      :resource="strategy.content_uuid"
      :collection="strategy.collection_uuid"
      :type="resourceType"
      :ariaLabel="`Remove ${strategy.content_body.source_strategy.title} from collection`"
    />
  </div>
</template>
